<template>
  <div class="p-4 bg-gray-100 min-h-screen">
    <Loader v-if="loading" />
    <Card class="shadow-2 surface-card border-round p-4">
      <template v-slot:title>
        <div class="text-2xl font-bold text-gray-800 flex align-items-center mb-4">
          <i class="pi pi-tags mr-2 text-primary"></i>
          Gestione Categorie Corsi
        </div>
      </template>

      <template v-slot:content>
        <DataTable :value="courseTypes" v-model:expandedRows="expandedRows" @row-expand="onRowExpand" stripedRows responsiveLayout="scroll">
          <Column expander style="width: 3rem" />
          <Column field="nome" header="Categoria Corso" sortable></Column>
          <Column field="total_athletes" header="Iscritti Totali" sortable style="width: 150px">
            <template #body="{ data }">
              <Badge :value="data.total_athletes" severity="info" />
            </template>
          </Column>
          <Column field="missing_docs_athletes" header="Mancano Certificati" sortable style="width: 180px">
            <template #body="{ data }">
              <Badge :value="data.missing_docs_athletes" :severity="data.missing_docs_athletes > 0 ? 'danger' : 'success'" />
            </template>
          </Column>
          <Column field="missing_docs_with_app_athletes" header="Notificabili (App)" sortable style="width: 150px">
            <template #body="{ data }">
              <div class="flex align-items-center gap-2">
                <Badge :value="data.missing_docs_with_app_athletes" severity="warning" />
                <Button 
                  icon="pi pi-users" 
                  class="p-button-rounded p-button-text p-button-sm" 
                  @click="openAthletesDialog(data)"
                  v-tooltip="'Vedi elenco atleti'"
                  :disabled="data.missing_docs_with_app_athletes === 0"
                />
              </div>
            </template>
          </Column>
          <Column header="Documenti Obbligatori">
            <template #body="{ data }">
              <div class="flex flex-wrap gap-2">
                <Chip 
                  v-for="doc in data.required_docs" 
                  :key="doc.id" 
                  :label="doc.nome + (doc.min_age ? ` (Da ${doc.min_age} anni)` : '') + (doc.max_age ? ` (Fino a ${doc.max_age} anni)` : '')" 
                  class="bg-primary text-white"
                  removable 
                  @remove="removeDoc(data, doc)"
                />
                <Button 
                  icon="pi pi-plus" 
                  class="p-button-rounded p-button-outlined p-button-sm" 
                  @click="openAddDocDialog(data)"
                  v-tooltip="'Aggiungi documento'"
                />
              </div>
            </template>
          </Column>
          <Column header="Azioni" style="width: 100px">
            <template #body="{ data }">
              <Button 
                icon="pi pi-bell" 
                class="p-button-rounded p-button-warning" 
                @click="sendNotification(data)"
                v-tooltip="'Invia notifica a chi mancano documenti'"
                :disabled="data.missing_docs_athletes === 0"
              />
            </template>
          </Column>
          
          <template #expansion="slotProps">
            <div class="p-4 bg-gray-50">
              <div class="text-xl font-bold mb-3">Dettaglio Corsi per {{ slotProps.data.nome }}</div>
              <DataTable :value="slotProps.data.corsi" class="p-datatable-sm">
                <Column field="nome" header="Corso"></Column>
                <Column field="total_athletes" header="Iscritti">
                  <template #body="{ data }">
                    <Badge :value="data.total_athletes" severity="info" />
                  </template>
                </Column>
                <Column field="missing_docs_athletes" header="Mancano Doc. (Totali)">
                  <template #body="{ data }">
                    <Badge :value="data.missing_docs_athletes" :severity="data.missing_docs_athletes > 0 ? 'danger' : 'success'" />
                  </template>
                </Column>
                <Column v-for="doc in slotProps.data.required_docs" :key="'col_doc_' + doc.id" :header="'Mancano ' + doc.nome">
                  <template #body="{ data }">
                    <Badge :value="data['missing_doc_' + doc.id] || 0" :severity="(data['missing_doc_' + doc.id] || 0) > 0 ? 'danger' : 'success'" />
                  </template>
                </Column>
                <Column field="athletes_with_app" header="In App">
                  <template #body="{ data }">
                    <Badge :value="data.athletes_with_app" severity="warning" />
                  </template>
                </Column>
                <Column header="Azioni" style="width: 15rem">
                  <template #body="{ data }">
                    <div class="flex gap-2">
                        <Button 
                            label="Notifica Corso"
                            icon="pi pi-bell" 
                            class="p-button-sm p-button-warning" 
                            @click="sendCourseNotification(data)"
                            :disabled="data.missing_docs_athletes === 0"
                        />
                        <Button 
                            label="Atleti"
                            icon="pi pi-users" 
                            class="p-button-sm p-button-info" 
                            @click="openCourseAthletesDialog(data)"
                            :disabled="data.missing_docs_athletes === 0"
                        />
                    </div>
                  </template>
                </Column>
              </DataTable>
            </div>
          </template>
        </DataTable>
      </template>
    </Card>

    <Dialog v-model:visible="addDocDialog.visible" header="Aggiungi Documento Obbligatorio" :modal="true" :style="{ width: '400px' }">
      <div class="flex flex-column gap-3">
        <label for="docSelect" class="font-bold">Seleziona Documento</label>
        <Dropdown 
          id="docSelect" 
          v-model="addDocDialog.selectedDoc" 
          :options="availableDocs" 
          optionLabel="nome" 
          placeholder="Seleziona un tipo di documento" 
          class="w-full"
        />
        <div class="flex gap-2 w-full mt-2">
            <div class="flex-1 flex flex-column gap-2">
                <label for="minAge" class="font-bold text-sm">Età Minima (opzionale)</label>
                <InputNumber id="minAge" v-model="addDocDialog.minAge" class="w-full" :min="0" :max="100" placeholder="Es. 7" />
            </div>
            <div class="flex-1 flex flex-column gap-2">
                <label for="maxAge" class="font-bold text-sm">Età Massima (opzionale)</label>
                <InputNumber id="maxAge" v-model="addDocDialog.maxAge" class="w-full" :min="0" :max="100" placeholder="Es. 6" />
            </div>
        </div>
      </div>
      <template #footer>
        <Button label="Annulla" icon="pi pi-times" class="p-button-text" @click="addDocDialog.visible = false" />
        <Button label="Salva" icon="pi pi-check" class="p-button-primary" @click="saveDoc" :disabled="!addDocDialog.selectedDoc" />
      </template>
    </Dialog>
    <Dialog v-model:visible="athletesDialog.visible" :header="athletesDialog.title" :modal="true" :style="{ width: '700px' }">
      <DataTable 
        :value="athletesDialog.athletes" 
        v-model:selection="athletesDialog.selectedAthletes" 
        dataKey="id"
        stripedRows 
        responsiveLayout="scroll"
        class="mb-3"
      >
        <Column selectionMode="multiple" headerStyle="width: 3rem"></Column>
        <Column field="cognome" header="Cognome" sortable></Column>
        <Column field="nome" header="Nome" sortable></Column>
        <Column field="datadinascita" header="Data di Nascita" sortable>
          <template #body="{ data }">
            {{ data.datadinascita ? moment(data.datadinascita).format('DD/MM/YYYY') : 'N/D' }}
          </template>
        </Column>
        <Column header="Età" sortable>
          <template #body="{ data }">
            {{ data.datadinascita ? moment().diff(moment(data.datadinascita), 'years') : 'N/D' }}
          </template>
        </Column>
        <Column header="Azioni" style="width: 100px">
          <template #body="{ data }">
            <Button 
                icon="pi pi-bell" 
                class="p-button-rounded p-button-warning p-button-sm" 
                @click="sendIndividualNotification(data)"
                v-tooltip="'Invia notifica singola'"
            />
          </template>
        </Column>
      </DataTable>
      <template #footer>
        <Button label="Chiudi" icon="pi pi-times" class="p-button-text" @click="athletesDialog.visible = false" />
        <Button 
          label="Invia a Selezionati" 
          icon="pi pi-bell" 
          class="p-button-warning" 
          @click="sendBulkNotification" 
          :disabled="!athletesDialog.selectedAthletes || athletesDialog.selectedAthletes.length === 0" 
        />
      </template>
    </Dialog>

    <ConfirmDialog></ConfirmDialog>
  </div>
</template>

<script>
import { ref, onMounted, getCurrentInstance, computed } from 'vue';
import Service from '@/services/levante-api/levante-api';
import Loader from "@/components/Loader.vue";
import { useToast } from 'primevue/usetoast';
import { useConfirm } from "primevue/useconfirm";
import moment from 'moment';

export default {
  name: 'CourseTypes',
  components: { Loader },
  setup() {
    const self = getCurrentInstance();
    self.$eventBus = self.appContext.config.globalProperties.$eventBus;
    const service = new Service(self.$eventBus);
    const toast = useToast();
    const confirm = useConfirm();
    
    const loading = ref(false);
    const courseTypes = ref([]);
    const allDocs = ref([]);
    
    const addDocDialog = ref({
      visible: false,
      selectedCourseType: null,
      selectedDoc: null,
      minAge: null,
      maxAge: null
    });

    const expandedRows = ref([]);

    const athletesDialog = ref({
      visible: false,
      title: '',
      athletes: [],
      selectedAthletes: []
    });

    const onRowExpand = async (event) => {
        const typeCorso = event.data;
        if (!typeCorso.corsi) {
            loading.value = true;
            try {
                const res = await service.getCorsiByType(typeCorso.id);
                typeCorso.corsi = res.items || res;
            } catch (error) {
                toast.add({ severity: 'error', summary: 'Errore', detail: 'Errore caricamento corsi', life: 3000 });
            } finally {
                loading.value = false;
            }
        }
    };

    const loadData = async () => {
      loading.value = true;
      try {
        const [typesRes, docsRes] = await Promise.all([
          service.getTypeCorso(),
          service.getTypesOfFiles()
        ]);
        courseTypes.value = (typesRes.items || typesRes).map(t => ({
          ...t,
          total_athletes: t.total_athletes || 0,
          missing_docs_athletes: t.missing_docs_athletes || 0,
          missing_docs_with_app_athletes: t.missing_docs_with_app_athletes || 0
        }));
        allDocs.value = docsRes.items || docsRes;
      } catch (error) {
        toast.add({ severity: 'error', summary: 'Errore', detail: 'Errore caricamento dati', life: 3000 });
      } finally {
        loading.value = false;
      }
    };

    const availableDocs = computed(() => {
      if (!addDocDialog.value.selectedCourseType) return [];
      const usedIds = addDocDialog.value.selectedCourseType.required_docs.map(d => d.id);
      return allDocs.value.filter(d => !usedIds.includes(d.id));
    });

    const openAddDocDialog = (courseType) => {
      addDocDialog.value.selectedCourseType = courseType;
      addDocDialog.value.selectedDoc = null;
      addDocDialog.value.minAge = null;
      addDocDialog.value.maxAge = null;
      addDocDialog.value.visible = true;
    };

    const saveDoc = async () => {
      if (!addDocDialog.value.selectedCourseType || !addDocDialog.value.selectedDoc) return;
      
      loading.value = true;
      try {
        let fd = new FormData();
        fd.append('id_type_corso', addDocDialog.value.selectedCourseType.id);
        fd.append('id_type_doc', addDocDialog.value.selectedDoc.id);
        if (addDocDialog.value.minAge !== null && addDocDialog.value.minAge !== undefined) fd.append('min_age', addDocDialog.value.minAge);
        if (addDocDialog.value.maxAge !== null && addDocDialog.value.maxAge !== undefined) fd.append('max_age', addDocDialog.value.maxAge);

        await service.addRequiredDoc(fd);
        toast.add({ severity: 'success', summary: 'Successo', detail: 'Documento aggiunto', life: 3000 });
        addDocDialog.value.visible = false;
        await loadData();
      } catch (error) {
        toast.add({ severity: 'error', summary: 'Errore', detail: 'Errore salvataggio', life: 3000 });
      } finally {
        loading.value = false;
      }
    };

    const removeDoc = async (courseType, doc) => {
       confirm.require({
            message: 'Rimuovere questo documento obbligatorio?',
            header: 'Conferma Rimozione',
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                loading.value = true;
                try {
                    let fd = new FormData();
                    fd.append('id_type_corso', courseType.id);
                    fd.append('id_type_doc', doc.id);
                    
                    await service.removeRequiredDoc(fd);
                    toast.add({ severity: 'success', summary: 'Successo', detail: 'Documento rimosso', life: 3000 });
                    await loadData();
                } catch (error) {
                    toast.add({ severity: 'error', summary: 'Errore', detail: 'Errore rimozione', life: 3000 });
                } finally {
                    loading.value = false;
                }
            }
        });
    };

    const openCourseAthletesDialog = async (course) => {
        athletesDialog.value.title = `Atleti notificabili - ${course.nome}`;
        athletesDialog.value.selectedAthletes = [];
        loading.value = true;
        try {
            const res = await service.getMissingDocsAthletesByCourse(course.id);
            athletesDialog.value.athletes = res.items || res;
            athletesDialog.value.visible = true;
        } catch (error) {
            toast.add({ severity: 'error', summary: 'Errore', detail: 'Errore caricamento atleti', life: 3000 });
        } finally {
            loading.value = false;
        }
    };

    const sendCourseNotification = (course) => {
        const title = 'Documento obbligatorio mancante';
        const body = 'Manca un documento obbligatorio per il tuo corso. Caricalo sull\'app per regolarizzare la tua posizione.';
        
        confirm.require({
            message: `Stai per inviare la seguente notifica a TUTTI gli atleti notificabili del corso ${course.nome}:\n\n"${body}"\n\nVuoi procedere?`,
            header: 'Conferma Invio Notifica Corso',
            icon: 'pi pi-bell',
            accept: async () => {
                loading.value = true;
                try {
                    const res = await service.sendMissingDocsNotificationByCourse(course.id);
                    const item = res.item || res;
                    if (item.esito === 'success') {
                        toast.add({ severity: 'success', summary: 'Successo', detail: item.messaggio, life: 3000 });
                    } else {
                        toast.add({ severity: 'warn', summary: 'Attenzione', detail: item.messaggio, life: 3000 });
                    }
                } catch (error) {
                    toast.add({ severity: 'error', summary: 'Errore', detail: 'Errore invio notifica', life: 3000 });
                } finally {
                    loading.value = false;
                }
            }
        });
    };

    const openAthletesDialog = async (courseType) => {
        athletesDialog.value.title = `Atleti notificabili - ${courseType.nome}`;
        athletesDialog.value.selectedAthletes = [];
        loading.value = true;
        try {
            const res = await service.getMissingDocsAthletes(courseType.id);
            athletesDialog.value.athletes = res.items || res;
            athletesDialog.value.visible = true;
        } catch (error) {
            toast.add({ severity: 'error', summary: 'Errore', detail: 'Errore caricamento atleti', life: 3000 });
        } finally {
            loading.value = false;
        }
    };

    const sendIndividualNotification = (athlete) => {
        const title = 'Documento obbligatorio mancante';
        const body = 'Manca un documento obbligatorio per il tuo corso. Caricalo sull\'app per regolarizzare la tua posizione.';
        
        confirm.require({
            message: `Inviare notifica a ${athlete.nome} ${athlete.cognome}?\n\n"${body}"`,
            header: 'Conferma Invio Notifica',
            icon: 'pi pi-bell',
            accept: async () => {
                loading.value = true;
                try {
                    let fd = new FormData();
                    fd.append('user_id', athlete.id_utente);
                    fd.append('title', title);
                    fd.append('body', body);
                    
                    const res = await service.saveNotifica(fd);
                    const item = res.item || res;
                    if (item.esito === 'success') {
                        toast.add({ severity: 'success', summary: 'Successo', detail: item.messaggio, life: 3000 });
                    } else {
                        toast.add({ severity: 'warn', summary: 'Attenzione', detail: item.messaggio, life: 3000 });
                    }
                } catch (error) {
                    toast.add({ severity: 'error', summary: 'Errore', detail: 'Errore invio notifica', life: 3000 });
                } finally {
                    loading.value = false;
                }
            }
        });
    };

    const sendBulkNotification = () => {
        const title = 'Documento obbligatorio mancante';
        const body = 'Manca un documento obbligatorio per il tuo corso. Caricalo sull\'app per regolarizzare la tua posizione.';
        const count = athletesDialog.value.selectedAthletes.length;
        
        confirm.require({
            message: `Stai per inviare la notifica a ${count} atleti selezionati. Vuoi procedere?`,
            header: 'Conferma Invio Massivo',
            icon: 'pi pi-bell',
            accept: async () => {
                loading.value = true;
                try {
                    const userIds = athletesDialog.value.selectedAthletes.map(a => a.id_utente);
                    let fd = new FormData();
                    fd.append('title', title);
                    fd.append('body', body);
                    // sendMessaggeGruppoSelezionati expects user_ids as an array. 
                    // However, getPostByUrl in levante-api helper uses axios.post(url, fd).
                    // If we use FormData, we might need a different handling for arrays.
                    // Let's check sendMessaggeGruppoSelezionati in levante-api.js
                    
                    const res = await service.sendMessaggeGruppoSelezionatiBulk(title, body, userIds);
                    const item = res.item || res;
                    if (item.esito === 'success') {
                        toast.add({ severity: 'success', summary: 'Successo', detail: item.messaggio, life: 3000 });
                        athletesDialog.value.visible = false;
                    } else {
                        toast.add({ severity: 'warn', summary: 'Attenzione', detail: item.messaggio, life: 3000 });
                    }
                } catch (error) {
                    toast.add({ severity: 'error', summary: 'Errore', detail: 'Errore invio notifiche', life: 3000 });
                } finally {
                    loading.value = false;
                }
            }
        });
    };

    const sendNotification = (courseType) => {
        const title = 'Documento obbligatorio mancante';
        const body = 'Manca un documento obbligatorio per il tuo corso. Caricalo sull\'app per regolarizzare la tua posizione.';
        
        confirm.require({
            message: `Stai per inviare la seguente notifica a TUTTI gli atleti (${courseType.missing_docs_with_app_athletes}) della categoria:\n\n"${body}"\n\nVuoi procedere?`,
            header: 'Conferma Invio Notifica',
            icon: 'pi pi-bell',
            accept: async () => {
                loading.value = true;
                try {
                    const res = await service.sendMissingDocsNotification(courseType.id);
                    const item = res.item || res;
                    if (item.esito === 'success') {
                        toast.add({ severity: 'success', summary: 'Successo', detail: item.messaggio, life: 3000 });
                    } else {
                        toast.add({ severity: 'warn', summary: 'Attenzione', detail: item.messaggio, life: 3000 });
                    }
                } catch (error) {
                    toast.add({ severity: 'error', summary: 'Errore', detail: 'Errore invio notifica', life: 3000 });
                } finally {
                    loading.value = false;
                }
            }
        });
    };

    onMounted(() => {
      loadData();
    });

    return {
      loading,
      courseTypes,
      addDocDialog,
      availableDocs,
      openAddDocDialog,
      saveDoc,
      removeDoc,
      sendNotification,
      athletesDialog,
      openAthletesDialog,
      sendIndividualNotification,
      sendBulkNotification,
      expandedRows,
      onRowExpand,
      openCourseAthletesDialog,
      sendCourseNotification,
      moment
    };
  }
}
</script>
